import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FiX, FiSearch } from 'react-icons/fi';
import axios from 'axios';
import ShopMediaUploadSection from './ShopMediaUploadSection.jsx';

/** Capitalize first letter of each word; "sg" (any case) becomes "SG". */
const capitalizeAddress = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str.trim().split(/\s+/).map((word) => {
        if (word.toLowerCase() === 'sg') return 'SG';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
};

/** Remove spaces; if exactly 8 digits, prefix with +65. */
const normalizeSingaporePhone = (str) => {
    if (!str || typeof str !== 'string') return str;
    const stripped = str.replace(/\s/g, '');
    if (/^\d{8}$/.test(stripped)) return '+65' + stripped;
    return stripped;
};

const inputClass = 'mt-1 block w-full rounded-md border border-gray-300 text-blue-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

const EditShopModal = ({ shop, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        shopName: shop.shopName,
        address: shop.address,
        postal: shop.postal,
        region: shop.region,
        area: shop.area,
        openHour: shop.openHour,
        closeHour: shop.closeHour,
        massagePrice1hr: shop.massagePrice1hr ?? 68,
        status: shop.status,
        shopType: shop.shopType || 'Shop',
        shopContact: Array.isArray(shop.shopContact) ? (shop.shopContact.length ? shop.shopContact : ['']) : (shop.shopContact ? [shop.shopContact] : ['']),
        shopWhatsapp: Array.isArray(shop.shopWhatsapp) ? (shop.shopWhatsapp.length ? shop.shopWhatsapp : ['']) : (shop.shopWhatsapp ? [shop.shopWhatsapp] : ['']),
        shopTelegram: Array.isArray(shop.shopTelegram) ? (shop.shopTelegram.length ? shop.shopTelegram : ['']) : (shop.shopTelegram ? [shop.shopTelegram] : ['']),
        shopCategory: shop.shopCategory || 'Massage',
        shopWebsite: shop.shopWebsite || '',
        isHidden: shop.isHidden ?? false,
        isUpdated: shop.isUpdated ?? false,
        remarks: shop.remarks || ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [members, setMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showMemberSearch, setShowMemberSearch] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState(shop.godMember || []);
    const [searchLoading, setSearchLoading] = useState(false);
    const [shopLogoUrls, setShopLogoUrls] = useState(shop.shopLogo || []);
    const [shopImageUrls, setShopImageUrls] = useState(shop.shopImage || []);
    const [shopPriceImageUrls, setShopPriceImageUrls] = useState(shop.shopPriceImage || []);

    // Fetch members with search
    const fetchMembers = async (search) => {
        try {
            setSearchLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers?search=${encodeURIComponent(search)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setMembers(response.data.data.filter(member => 
                    !selectedMembers.find(m => m._id === member._id)
                ));
            }
        } catch (err) {
            console.error('Error fetching members:', err);
        } finally {
            setSearchLoading(false);
        }
    };

    // Debounced search effect
    useEffect(() => {
        if (!searchTerm) {
            setMembers([]);
            return;
        }

        const delayDebounceFn = setTimeout(() => {
            fetchMembers(searchTerm);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, selectedMembers]); // Include selectedMembers in dependencies

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const dataToSubmit = {
                ...formData,
                massagePrice1hr: formData.massagePrice1hr !== '' && formData.massagePrice1hr != null ? Number(formData.massagePrice1hr) : 68,
                shopContact: (formData.shopContact || []).filter(Boolean),
                shopWhatsapp: (formData.shopWhatsapp || []).filter(Boolean),
                shopTelegram: (formData.shopTelegram || []).filter(Boolean),
                godMember: selectedMembers.map(member => member._id)
            };

            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/shops/${shop._id}`,
                dataToSubmit,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );

            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            const errorData = err.response?.data;
            if (errorData?.conflicts) {
                setError({
                    message: errorData.error,
                    conflicts: errorData.conflicts
                });
            } else {
                setError(errorData?.error || err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleArrayChange = (field, index, value) => {
        setFormData(prev => {
            const arr = [...(prev[field] || [])];
            arr[index] = value;
            return { ...prev, [field]: arr };
        });
    };

    const handleAddArrayRow = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: [...(prev[field] || []), '']
        }));
    };

    const handleRemoveArrayRow = (field, index) => {
        setFormData(prev => {
            const arr = (prev[field] || []).filter((_, i) => i !== index);
            return { ...prev, [field]: arr.length ? arr : [''] };
        });
    };

    const handlePhoneBlur = (field, index) => {
        const arr = formData[field] || [];
        const raw = arr[index];
        const normalized = normalizeSingaporePhone(raw);
        if (normalized !== raw) {
            handleArrayChange(field, index, normalized);
        }
    };

    const handleMemberSearch = (e) => {
        setSearchTerm(e.target.value);
        setShowMemberSearch(true);
    };

    const handleSelectMember = (member) => {
        if (!selectedMembers.find(m => m._id === member._id)) {
            setSelectedMembers([...selectedMembers, member]);
            setMembers(members.filter(m => m._id !== member._id));
        }
        setSearchTerm('');
        setShowMemberSearch(false);
    };

    const handleRemoveMember = (memberId) => {
        setSelectedMembers(selectedMembers.filter(m => m._id !== memberId));
    };

    const filteredMembers = members;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-20 mx-auto p-5 border w-[700px] shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Edit Shop</h3>
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" id="isHidden" name="isHidden" checked={formData.isHidden} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:border-blue-500" />
                            <span className="text-sm text-gray-700">Hidden from listing</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" id="isUpdated" name="isUpdated" checked={formData.isUpdated} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:border-blue-500" />
                            <span className="text-sm text-gray-700">Updated</span>
                        </label>
                        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error.message || error}
                        {error.conflicts && error.conflicts.map((conflict, index) => (
                            <div key={index} className="mt-2 text-sm">
                                {conflict.memberFullName} already assigned to shop: {conflict.shopName}
                            </div>
                        ))}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Shop Name</label>
                            <input type="text" name="shopName" value={formData.shopName} onChange={handleChange} className={inputClass} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Shop Website</label>
                            <input type="url" name="shopWebsite" value={formData.shopWebsite} onChange={handleChange} placeholder="https://" className={inputClass} />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Address</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            onBlur={(e) => {
                                const capped = capitalizeAddress(e.target.value);
                                if (capped !== e.target.value) setFormData(prev => ({ ...prev, address: capped }));
                            }}
                            className={inputClass}
                            required
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                            <input type="text" name="postal" value={formData.postal} onChange={handleChange} className={inputClass} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Region</label>
                            <input type="text" name="region" value={formData.region} onChange={handleChange} className={inputClass} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Area</label>
                            <input type="text" name="area" value={formData.area} onChange={handleChange} className={inputClass} required />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Opening Time</label>
                            <input type="time" name="openHour" value={formData.openHour} onChange={handleChange} className={inputClass} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Closing Time</label>
                            <input type="time" name="closeHour" value={formData.closeHour} onChange={handleChange} className={inputClass} required />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Massage Price (1hr)</label>
                        <input
                            type="number"
                            name="massagePrice1hr"
                            min={0}
                            step={1}
                            value={formData.massagePrice1hr}
                            onChange={handleChange}
                            className={inputClass}
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <select name="status" value={formData.status} onChange={handleChange} className={inputClass} required>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="closed">Closed</option>
                                <option value="premium">Premium</option>
                                <option value="feature">Feature</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Shop Type</label>
                            <select name="shopType" value={formData.shopType} onChange={handleChange} className={inputClass}>
                                <option value="Shop">Shop</option>
                                <option value="Condo">Condo</option>
                                <option value="Hotel">Hotel</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Shop Category</label>
                            <select name="shopCategory" value={formData.shopCategory} onChange={handleChange} className={inputClass}>
                                <option value="Massage">Massage</option>
                                <option value="TCM">TCM</option>
                                <option value="Salon">Salon</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Shop Contact</label>
                            {(formData.shopContact || ['']).map((val, i) => (
                                <div key={`contact-${i}`} className="flex gap-1 mt-1 mb-1">
                                    <input type="text" value={val} onChange={(e) => handleArrayChange('shopContact', i, e.target.value)} onBlur={() => handlePhoneBlur('shopContact', i)} className="block flex-1 rounded-md border border-gray-300 text-blue-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-0" />
                                    <button type="button" onClick={() => handleRemoveArrayRow('shopContact', i)} className="text-red-600 hover:text-red-800 px-1 shrink-0">×</button>
                                </div>
                            ))}
                            <button type="button" onClick={() => handleAddArrayRow('shopContact')} className="text-sm text-blue-600 hover:underline">+ Add</button>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Shop WhatsApp</label>
                            {(formData.shopWhatsapp || ['']).map((val, i) => (
                                <div key={`wa-${i}`} className="flex gap-1 mt-1 mb-1">
                                    <input type="text" value={val} onChange={(e) => handleArrayChange('shopWhatsapp', i, e.target.value)} onBlur={() => handlePhoneBlur('shopWhatsapp', i)} className="block flex-1 rounded-md border border-gray-300 text-blue-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-0" />
                                    <button type="button" onClick={() => handleRemoveArrayRow('shopWhatsapp', i)} className="text-red-600 hover:text-red-800 px-1 shrink-0">×</button>
                                </div>
                            ))}
                            <button type="button" onClick={() => handleAddArrayRow('shopWhatsapp')} className="text-sm text-blue-600 hover:underline">+ Add</button>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Shop Telegram</label>
                            {(formData.shopTelegram || ['']).map((val, i) => (
                                <div key={`tg-${i}`} className="flex gap-1 mt-1 mb-1">
                                    <input type="text" value={val} onChange={(e) => handleArrayChange('shopTelegram', i, e.target.value)} className="block flex-1 rounded-md border border-gray-300 text-blue-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-0" />
                                    <button type="button" onClick={() => handleRemoveArrayRow('shopTelegram', i)} className="text-red-600 hover:text-red-800 px-1 shrink-0">×</button>
                                </div>
                            ))}
                            <button type="button" onClick={() => handleAddArrayRow('shopTelegram')} className="text-sm text-blue-600 hover:underline">+ Add</button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">God Members</label>
                        <div className="mt-1 relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleMemberSearch}
                                placeholder="Search God Members..."
                                className={inputClass}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <FiSearch className="text-gray-400" />
                            </div>
                        </div>
                        
                        {selectedMembers.length > 0 && (
                            <div className="mt-2 space-y-2">
                                {selectedMembers.map(member => (
                                    <div key={member._id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                        <span>{member.fullName}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveMember(member._id)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <FiX className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {showMemberSearch && searchTerm && (
                            <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg">
                                {searchLoading ? (
                                    <div className="p-3 text-center text-gray-500">Loading...</div>
                                ) : filteredMembers.length > 0 ? (
                                    <ul className="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                        {filteredMembers.map(member => (
                                            <li
                                                key={member._id}
                                                onClick={() => handleSelectMember(member)}
                                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                                            >
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{member.fullName}</span>
                                                    <span className="text-sm text-gray-500">
                                                        ID: {member.godID} | Web ID: {member.webID}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="p-3 text-center text-gray-500">No members found</div>
                                )}
                            </div>
                        )}
                    </div>

                    <ShopMediaUploadSection
                        label="Shop Logo"
                        mediaType="shopLogo"
                        urls={shopLogoUrls}
                        onUrlsChange={setShopLogoUrls}
                        shopId={shop._id}
                        multiple={false}
                    />
                    <ShopMediaUploadSection
                        label="Shop Images"
                        mediaType="shopImage"
                        urls={shopImageUrls}
                        onUrlsChange={setShopImageUrls}
                        shopId={shop._id}
                        multiple={true}
                    />
                    <ShopMediaUploadSection
                        label="Shop Price Images"
                        mediaType="shopPriceImage"
                        urls={shopPriceImageUrls}
                        onUrlsChange={setShopPriceImageUrls}
                        shopId={shop._id}
                        multiple={true}
                    />

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Remarks</label>
                        <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows={3} className={inputClass} />
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

EditShopModal.propTypes = {
    shop: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        shopName: PropTypes.string.isRequired,
        address: PropTypes.string.isRequired,
        postal: PropTypes.string.isRequired,
        region: PropTypes.string.isRequired,
        area: PropTypes.string.isRequired,
        openHour: PropTypes.string.isRequired,
        closeHour: PropTypes.string.isRequired,
        massagePrice1hr: PropTypes.number,
        status: PropTypes.string.isRequired,
        shopType: PropTypes.string,
        shopContact: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        shopWhatsapp: PropTypes.arrayOf(PropTypes.string),
        shopTelegram: PropTypes.arrayOf(PropTypes.string),
        shopCategory: PropTypes.string,
        shopWebsite: PropTypes.string,
        isHidden: PropTypes.bool,
        isUpdated: PropTypes.bool,
        shopLogo: PropTypes.arrayOf(PropTypes.string),
        shopImage: PropTypes.arrayOf(PropTypes.string),
        shopPriceImage: PropTypes.arrayOf(PropTypes.string),
        remarks: PropTypes.string,
        godMember: PropTypes.array
    }).isRequired,
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default EditShopModal; 